import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import {
  searchOneToOne,
  searchGroup,
  searchCrashCourse,
  type CommonOfferingSearchParams,
  type GroupOfferingSearchParams,
  type CrashCourseSearchParams,
  type OfferingSearchPageResponse,
} from '@/services/offeringSearchService';

/** Next page number, or undefined once the backend's last page has been reached. */
function nextOfferingPage(lastPage: OfferingSearchPageResponse): number | undefined {
  return lastPage.number + 1 < lastPage.totalPages ? lastPage.number + 1 : undefined;
}

export function useOneToOneSearch(params: CommonOfferingSearchParams, enabled = true) {
  return useQuery<OfferingSearchPageResponse>({
    queryKey: ['offerings', 'one-to-one', params],
    queryFn: () => searchOneToOne(params),
    enabled,
    staleTime: 30_000,
  });
}

export function useGroupSearch(params: GroupOfferingSearchParams, enabled = true) {
  return useQuery<OfferingSearchPageResponse>({
    queryKey: ['offerings', 'group', params],
    queryFn: () => searchGroup(params),
    enabled,
    staleTime: 30_000,
  });
}

export function useCrashCourseSearch(params: CrashCourseSearchParams, enabled = true) {
  return useQuery<OfferingSearchPageResponse>({
    queryKey: ['offerings', 'crash-course', params],
    queryFn: () => searchCrashCourse(params),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * "Load more" variants for the /search/* pages (ADR-088). Params must omit
 * `page` — the page number is threaded through by React Query itself as the
 * infinite-query page param, and the accumulated pages live at `data.pages`
 * rather than a single `data` object. Kept separate from `useOneToOneSearch`
 * et al. above rather than changing them in place, since several other
 * call sites (dashboard rails, the inquiry composer, landing carousels) rely
 * on today's plain single-page `useQuery` shape.
 */
export function useOneToOneSearchInfinite(params: Omit<CommonOfferingSearchParams, 'page'>, enabled = true) {
  return useInfiniteQuery<OfferingSearchPageResponse>({
    queryKey: ['offerings', 'one-to-one', 'infinite', params],
    queryFn: ({ pageParam }) => searchOneToOne({ ...params, page: pageParam as number }),
    initialPageParam: 0,
    getNextPageParam: nextOfferingPage,
    enabled,
    staleTime: 30_000,
  });
}

export function useGroupSearchInfinite(params: Omit<GroupOfferingSearchParams, 'page'>, enabled = true) {
  return useInfiniteQuery<OfferingSearchPageResponse>({
    queryKey: ['offerings', 'group', 'infinite', params],
    queryFn: ({ pageParam }) => searchGroup({ ...params, page: pageParam as number }),
    initialPageParam: 0,
    getNextPageParam: nextOfferingPage,
    enabled,
    staleTime: 30_000,
  });
}

export function useCrashCourseSearchInfinite(params: Omit<CrashCourseSearchParams, 'page'>, enabled = true) {
  return useInfiniteQuery<OfferingSearchPageResponse>({
    queryKey: ['offerings', 'crash-course', 'infinite', params],
    queryFn: ({ pageParam }) => searchCrashCourse({ ...params, page: pageParam as number }),
    initialPageParam: 0,
    getNextPageParam: nextOfferingPage,
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Landing "Popular Group Classes" carousel — ranked by rolling-30-day view count,
 * limited to batches starting soon (ADR-075/096). Replaces the old /offerings/group
 * discovery endpoint hook. Loads a page at a time via "load more".
 */
export function usePopularGroupClasses(size = 8, startsWithinDays = 30) {
  return useGroupSearchInfinite({ sortBy: 'POPULARITY', size, startsWithinDays });
}

/** Landing "Popular Crash Courses" carousel — same ranking + start window. Loads a page at a time via "load more". */
export function usePopularCrashCourses(size = 8, startsWithinDays = 30) {
  return useCrashCourseSearchInfinite({ sortBy: 'POPULARITY', size, startsWithinDays });
}
